
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Helmet, HelmetProvider } from "react-helmet-async"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import api from "../api"
import { ReactComponent as AddIcon } from "../assets/images/icons/ic-add.svg"
import { ReactComponent as CheckIcon } from "../assets/images/icons/ic-check.svg"
import { ReactComponent as ChevronIcon } from "../assets/images/icons/ic-chevron.svg"
import { ReactComponent as DeleteIcon } from "../assets/images/icons/ic-delete.svg"
import { ReactComponent as EditIcon } from "../assets/images/icons/ic-edit.svg"
import { ReactComponent as ExerciseIcon } from "../assets/images/icons/ic-exercise-inactive.svg"
import { ReactComponent as EmptyTests } from "../assets/images/illustrations/3D/empty-test.svg"
import { Each } from "../common/Each"
import Button from "../components/Button"
import Card from "../components/cards/Card"
import EmptyContent from "../components/EmptyContent.js"
import MeatBallsMenu from "../components/MeatBallsMenu"
import Table from "../components/Table"
import AlertDialog from "../components/dialogs/AlertDialog"
import HeaderFooterLayout from "../components/layouts/HeaderFooterLayout"
import Skeleton from "../components/Skeleton"
import TextInput from "../components/TextInput"
import { DialogStatus, TestStatus, TestType } from "../enums"
import typo from "../typography.module.css"
import { getDefaultTest } from "../utils.js"
import Test from "./Test.js"
import styles from "./Tests.module.css"

const mergeCollapsedState = (previousState, nextIds, defaultCollapsed = true) => {
    const current = new Map(previousState.map((item) => [item.id, item.collapsed]))
    return nextIds.map((id) => ({
        id,
        collapsed: current.has(id) ? current.get(id) : defaultCollapsed,
    }))
}

const getQuestionsCount = (test) => {
    if (!test?.content?.innerTests) {
        return 0
    }

    let count = 0
    for (const innerTest of test.content.innerTests) {
        const testType = innerTest?.testType
        switch (testType) {
            case TestType.SingleChoice:
            case TestType.MultipleChoice:
                count += 1
                break
            case TestType.TrueFalse:
                count += innerTest?.answers?.length > 0 ? innerTest.answers.length : 0
                break
            case TestType.TextCompletion:
                if (innerTest?.words?.length > 0) {
                    count += innerTest.words.reduce((acc, word) => acc + (word.hidden ? 1 : 0), 0)
                }
                break
            default:
                break
        }
    }

    return count
}

const getStatusMeta = (test, t) => {
    if (!test) {
        return { label: "-", color: "var(--sf-gray)" }
    }

    if (test.deleted_at) {
        return { label: t("tests.status.deleted"), color: "#fa6675" }
    }

    if (test.status === TestStatus.Draft) {
        if (test.publishable) {
            return { label: t("tests.publishable"), color: "var(--tertiary)" }
        }
        return { label: t("tests.toBeCompleted"), color: "var(--secondary)" }
    }

    if (test.status === TestStatus.Completed) {
        return { label: t("tests.status.completed"), color: "#2fc6a0" }
    }

    if (test.status === TestStatus.Public) {
        const isStarted = test.published_at ? new Date(test.published_at) <= new Date() : false
        return {
            label: isStarted ? "IN CORSO" : "PROGRAMMATO",
            color: isStarted ? "var(--secondary)" : "var(--sf-gray)",
        }
    }

    return { label: test.status ?? "-", color: "var(--sf-gray)" }
}

const groupTestsByCourseEdition = (tests, t) => {
    const grouped = tests.reduce((acc, test) => {
        const course = test?.module?.course
        const edition = test?.module?.edition

        const courseId = course?.id ?? "no-course"
        if (!acc[courseId]) {
            acc[courseId] = {
                course: {
                    id: courseId,
                    name: course?.name ?? t("tests.noModuleGroup"),
                    thumbnail: course?.thumbnail ?? null,
                },
                editionsMap: {},
            }
        }

        const editionId = edition?.id ?? `no-edition-${courseId}`
        if (!acc[courseId].editionsMap[editionId]) {
            acc[courseId].editionsMap[editionId] = {
                edition: {
                    id: editionId,
                    name: edition?.name ?? t("tests.module.empty"),
                },
                tests: [],
            }
        }

        acc[courseId].editionsMap[editionId].tests.push(test)
        return acc
    }, {})

    return Object.values(grouped)
        .map((courseGroup) => ({
            ...courseGroup,
            editions: Object.values(courseGroup.editionsMap)
                .map((editionGroup) => ({
                    ...editionGroup,
                    tests: [...editionGroup.tests].sort((a, b) => {
                        if (!a.deleted_at && b.deleted_at) return -1
                        if (a.deleted_at && !b.deleted_at) return 1
                        return (a.name ?? "").localeCompare(b.name ?? "", undefined, { sensitivity: "base" })
                    }),
                }))
                .sort((a, b) => (a.edition?.name ?? "").localeCompare(b.edition?.name ?? "", undefined, { sensitivity: "base" })),
        }))
        .sort((a, b) => (a.course?.name ?? "").localeCompare(b.course?.name ?? "", undefined, { sensitivity: "base" }))
}

const Tests = () => {

    const { t } = useTranslation()
    const navigate = useNavigate()
    const [tests, setTests] = useState([]);
    const [filteredTests, setFilteredTests] = useState([]);
    const [modules, setModules] = useState([])
    const [testsLoading, setTestsLoading] = useState(false)
    const [showAddPage, setShowAddPage] = useState(false)
    const [showEditPage, setShowEditPage] = useState(false)
    const [selectedTest, setSelectedTest] = useState(getDefaultTest())
    const [courseCollapsableState, setCourseCollapsableState] = useState([])
    const [selectedEditionByCourse, setSelectedEditionByCourse] = useState({})

    const [openAlert, setOpenAlert] = useState(false)
    const [alertStatus, setAlertStatus] = useState(DialogStatus.Default)
    const [selectedDeleteTest, setSelectedDeleteTest] = useState(null)

    const [openCompleteAlert, setOpenCompleteAlert] = useState(false)
    const [completeAlertStatus, setCompleteAlertStatus] = useState(DialogStatus.Default)
    const [selectedCompleteTest, setSelectedCompleteTest] = useState(null)

    const deleteCloseTimeoutRef = useRef(null)
    const completeCloseTimeoutRef = useRef(null)

    const groupedTests = useMemo(() => groupTestsByCourseEdition(filteredTests, t), [filteredTests, t])

    const syncCollapsedStateFromGroups = useCallback((groups) => {
        const courseIds = groups.map((group) => group.course.id)

        setCourseCollapsableState((prev) => mergeCollapsedState(prev, courseIds, true))
        setSelectedEditionByCourse((prev) => {
            const next = {}

            for (const group of groups) {
                const courseId = group.course.id
                const validEditionIds = group.editions.map((editionGroup) => editionGroup.edition.id)
                const previousSelection = prev[courseId]
                next[courseId] = validEditionIds.includes(previousSelection) ? previousSelection : null
            }

            return next
        })
    }, [])

    const getTests = useCallback(async () => {
        setTestsLoading(true);
        try {
            const tests = await api.get("/teacher/tests");
            setTests(tests);
            setFilteredTests(tests);
        } catch (e) {
            console.error(e);
        }
        setTestsLoading(false);
    }, []);

    useEffect(() => {
        const getModules = async () => {
            try {
                const lessons = await api.get("/teacher/lessons");
                const lessonsPerModule = {}

                // group lessons by moduleId
                for (const lesson of lessons) {
                    if (lesson.module.id in lessonsPerModule) {
                        lessonsPerModule[lesson.module.id].lessons.push(lesson)
                    } else {
                        lessonsPerModule[lesson.module.id] = {
                            id: lesson.module.id,
                            label: lesson.module.name,
                            subtitle: `${lesson.edition?.course?.name} - ${lesson.edition?.name}`,
                            lessons: [lesson]
                        }
                    }
                }
                const modules = Object.values(lessonsPerModule)
                modules.unshift({ id: null, label: t("tests.module.empty") })
                setModules(modules)
            } catch (e) {
                console.error(e);
            }
        };

        getModules()
        getTests();
    }, [getTests, t]);

    useEffect(() => {
        syncCollapsedStateFromGroups(groupedTests)
    }, [groupedTests, syncCollapsedStateFromGroups])

    useEffect(() => {
        window.scrollTo(0, 0)
    }, [showAddPage, showEditPage])

    const onSearch = useCallback((text) => {
        if (tests && tests.length > 0) {
            if (text) {
                const filter = tests.filter((s) => {
                    const target = `${s.name} ${s.module?.name ?? ""} ${s.module?.course?.name ?? ""} ${s.module?.edition?.name ?? ""} ${s.lesson?.name ?? ""}`.toLowerCase().replaceAll(" ", "");
                    const query = text ? text.toLowerCase().replaceAll(" ", "") : text
                    return target.includes(query);
                });
                setFilteredTests(filter);
            } else {
                setFilteredTests(tests);
            }
        }
    }, [tests])

    const onEditAction = useCallback((test) => {
        setSelectedTest({ ...test })
        setShowEditPage(true)
    }, [])

    const deleteTest = useCallback(async () => {
        if (!selectedDeleteTest) {
            return
        }

        try {
            setAlertStatus(DialogStatus.Loading)
            await api.delete(`/teacher/tests/${selectedDeleteTest.id}`)
            await getTests()
            setAlertStatus(DialogStatus.Success)
            deleteCloseTimeoutRef.current = setTimeout(() => {
                setOpenAlert(false)
                setAlertStatus(DialogStatus.Default)
                setSelectedDeleteTest(null)
            }, 1200)
        } catch (e) {
            console.error(e)
            setAlertStatus(DialogStatus.Error)
        }
    }, [getTests, selectedDeleteTest])

    const completeTest = useCallback(async () => {
        if (!selectedCompleteTest) {
            return
        }

        try {
            setCompleteAlertStatus(DialogStatus.Loading)
            await api.put(`/teacher/tests/${selectedCompleteTest.id}`, { status: TestStatus.Completed })
            await getTests()
            setCompleteAlertStatus(DialogStatus.Success)
            completeCloseTimeoutRef.current = setTimeout(() => {
                setOpenCompleteAlert(false)
                setCompleteAlertStatus(DialogStatus.Default)
                setSelectedCompleteTest(null)
            }, 1200)
        } catch (e) {
            console.error(e)
            setCompleteAlertStatus(DialogStatus.Error)
        }
    }, [getTests, selectedCompleteTest])

    const getMenuActions = useCallback((test) => {
        if (!test || test.deleted_at) {
            return []
        }

        const actions = [
            {
                label: t("tests.delete"),
                icon: DeleteIcon,
                style: { color: "var(--secondary)" },
                onClick: () => {
                    setSelectedDeleteTest(test)
                    setOpenAlert(true)
                },
            },
            {
                label: t("tests.edit"),
                icon: EditIcon,
                style: { color: "var(--primary)" },
                onClick: () => onEditAction(test),
            },
        ]

        if (test.status === TestStatus.Public) {
            actions.unshift({
                label: t("tests.end"),
                icon: CheckIcon,
                style: { color: "var(--primary)" },
                onClick: () => {
                    setSelectedCompleteTest(test)
                    setOpenCompleteAlert(true)
                },
            })
        }

        if (test.status === TestStatus.Public || test.status === TestStatus.Completed) {
            actions.unshift({
                label: t("tests.open"),
                icon: ExerciseIcon,
                style: { color: "var(--tertiary)" },
                onClick: () => navigate(`/tests/${test.id}`),
            })
        }

        return actions
    }, [navigate, onEditAction, t])

    const onEdit = useCallback(() => {
        setShowEditPage(false)
        getTests()
    }, [getTests])

    const onAdd = useCallback(() => {
        setShowAddPage(false)
        getTests()
    }, [getTests])

    return (
        <HeaderFooterLayout>
            <HelmetProvider>
                <Helmet>
                    <title>Test</title>
                </Helmet>
            </HelmetProvider>
            <div className={styles.container}>
                <div className={styles.section}>
                    <div className={styles.sectionInner}>
                        {
                            showAddPage === false && showEditPage === false &&
                            <div className={styles.content}>
                                <div className={styles.header}>
                                    <div className={typo.title}>{t("tests.title")}</div>


                                    <div className={styles.add}>
                                        <Button
                                            appearance="subtle"
                                            onClick={() => setShowAddPage(true)}
                                        >
                                            <AddIcon />
                                            {t("tests.addNewTest").toUpperCase()}
                                        </Button>
                                    </div>
                                </div>
                                <TextInput
                                    onKeyUp={onSearch}
                                    type="search"
                                    placeholder={t("search")}
                                    style={{
                                        backgroundColor: "var(--background-secondary-color)",
                                    }} />
                                <div className={styles.modules}>
                                    {
                                        testsLoading === true &&
                                        <Each
                                            of={[1, 2]}
                                            render={() => (
                                                <Card>
                                                    <div className={styles.courseSkeleton}>
                                                        <div className={styles.courseSkeletonHeader}>
                                                            <Skeleton type="rect" width="256px" height={"22px"} borderRadius={"8px"} />
                                                        </div>
                                                    </div>
                                                </Card>
                                            )}
                                        />
                                    }

                                    {
                                        testsLoading === false && groupedTests.length > 0 &&
                                        <Each
                                            of={groupedTests}
                                            render={(courseGroup) => {
                                                const isCourseCollapsed = courseCollapsableState.find((item) => item.id === courseGroup.course.id)?.collapsed
                                                const selectedEditionId = selectedEditionByCourse[courseGroup.course.id] ?? null
                                                const selectedEditionGroup = selectedEditionId
                                                    ? courseGroup.editions.find((editionGroup) => editionGroup.edition.id === selectedEditionId)
                                                    : null
                                                const testsToShow = selectedEditionGroup
                                                    ? selectedEditionGroup.tests
                                                    : courseGroup.editions.flatMap((editionGroup) => editionGroup.tests)

                                                return (
                                                    <Card
                                                        title={
                                                            <div
                                                                className={styles.courseHeader}
                                                                onClick={() => {
                                                                    setCourseCollapsableState((prev) => prev.map((item) =>
                                                                        item.id === courseGroup.course.id ? { ...item, collapsed: !item.collapsed } : item
                                                                    ))
                                                                }}
                                                            >
                                                                {
                                                                    courseGroup.course.thumbnail
                                                                        ? <img src={courseGroup.course.thumbnail} alt="" className={styles.courseThumbnail} />
                                                                        : <div className={styles.courseThumbnailFallback}>{courseGroup.course.name?.charAt(0) ?? "-"}</div>
                                                                }
                                                                <div className={styles.courseTitle}>{courseGroup.course.name}</div>
                                                            </div>
                                                        }
                                                    >
                                                        <ChevronIcon
                                                            className={`${styles.courseChevron} ${isCourseCollapsed ? styles.collapsed : ''}`}
                                                            onClick={() => {
                                                                setCourseCollapsableState((prev) => prev.map((item) =>
                                                                    item.id === courseGroup.course.id ? { ...item, collapsed: !item.collapsed } : item
                                                                ))
                                                            }}
                                                        />

                                                        <div className={`${styles.courseBody} ${isCourseCollapsed ? styles.collapsed : ''}`}>
                                                            <div className={styles.editionFilters}>
                                                                <button
                                                                    className={`${styles.editionFilter} ${selectedEditionId === null ? styles.selected : ''}`}
                                                                    onClick={() => {
                                                                        setSelectedEditionByCourse((prev) => ({ ...prev, [courseGroup.course.id]: null }))
                                                                    }}
                                                                >
                                                                    Tutto
                                                                </button>
                                                                <Each
                                                                    of={courseGroup.editions}
                                                                    render={(editionGroup) => (
                                                                        <button
                                                                            className={`${styles.editionFilter} ${selectedEditionId === editionGroup.edition.id ? styles.selected : ''}`}
                                                                            onClick={() => {
                                                                                setSelectedEditionByCourse((prev) => ({ ...prev, [courseGroup.course.id]: editionGroup.edition.id }))
                                                                            }}
                                                                        >
                                                                            {editionGroup.edition.name}
                                                                        </button>
                                                                    )}
                                                                />
                                                            </div>

                                                            <Table
                                                                appearance="minimal"
                                                                columns={[
                                                                    { key: 'name', label: t("tests.name"), type: 'string' },
                                                                    { key: 'module_name', label: 'MODULO', type: 'custom' },
                                                                    { key: 'lesson_name', label: t("tests.lesson.label"), type: 'custom' },
                                                                    { key: 'questions_count', label: t("tests.questions"), type: 'number' },
                                                                    { key: 'published_at', label: t("tests.publishedAt"), type: 'datetime' },
                                                                    { key: 'expires_at', label: t("tests.expiresAt"), type: 'datetime' },
                                                                    { key: '_status', label: 'STATO', type: 'custom' },
                                                                    { key: '_actions', label: 'ACTIONS', type: 'custom' },
                                                                ]}
                                                                elements={testsToShow.map((test) => {
                                                                    const statusMeta = getStatusMeta(test, t)
                                                                    return {
                                                                        ...test,
                                                                        module_name: {
                                                                            value: test.module?.name ?? '-',
                                                                            type: 'string',
                                                                            component: <div className={styles.clampedLabel}>{test.module?.name ?? '-'}</div>,
                                                                        },
                                                                        lesson_name: {
                                                                            value: test.lesson?.name ?? 'Nessuna',
                                                                            type: 'string',
                                                                            component: <div className={styles.clampedLabel}>{test.lesson?.name ?? 'Nessuna'}</div>,
                                                                        },
                                                                        questions_count: getQuestionsCount(test),
                                                                        _status: {
                                                                            value: statusMeta.label,
                                                                            type: 'string',
                                                                            component: (
                                                                                <div className={styles.statusBadge} style={{ '--badge-color': statusMeta.color }}>
                                                                                    {statusMeta.label}
                                                                                </div>
                                                                            ),
                                                                        },
                                                                        _actions: {
                                                                            value: test.id,
                                                                            type: 'number',
                                                                            component: (
                                                                                <div className={styles.actionCell}>
                                                                                    {!test.deleted_at && <MeatBallsMenu actions={getMenuActions(test)} />}
                                                                                </div>
                                                                            ),
                                                                        },
                                                                    }
                                                                })}
                                                            />
                                                        </div>
                                                    </Card>
                                                )
                                            }}
                                        />
                                    }

                                    {
                                        testsLoading === false && groupedTests.length === 0 &&
                                        <EmptyContent
                                            Illustration={EmptyTests}
                                            message={"Non ci sono ancora test qui, creane uno nuovo per vederlo in lista."}
                                            imageStyle={{ width: "192px" }}
                                        />
                                    }
                                </div>
                            </div>
                        }
                        {
                            showAddPage === true &&
                            <Test prefetchedModules={modules} onBack={() => setShowAddPage(false)} onSave={onAdd} />
                        }
                        {
                            showEditPage === true &&
                            <Test prefetchedModules={modules} onBack={() => setShowEditPage(false)} onSave={onEdit} test={selectedTest} />
                        }
                    </div>
                </div>
            </div>

            <AlertDialog
                open={openAlert}
                title={t("tests.deleteTitle")}
                text={t("tests.deleteText")}
                status={alertStatus}
                onClose={() => {
                    setOpenAlert(false)
                    setAlertStatus(DialogStatus.Default)
                    setSelectedDeleteTest(null)
                    if (deleteCloseTimeoutRef.current) {
                        clearTimeout(deleteCloseTimeoutRef.current)
                    }
                }}
                actions={[
                    {
                        label: t("tests.deleteConfirm").toUpperCase(),
                        onClick: deleteTest,
                    },
                ]}
            />

            <AlertDialog
                open={openCompleteAlert}
                title={t("tests.completeTitle")}
                text={t("tests.completeText")}
                status={completeAlertStatus}
                onClose={() => {
                    setOpenCompleteAlert(false)
                    setCompleteAlertStatus(DialogStatus.Default)
                    setSelectedCompleteTest(null)
                    if (completeCloseTimeoutRef.current) {
                        clearTimeout(completeCloseTimeoutRef.current)
                    }
                }}
                actions={[
                    {
                        label: t("tests.completeConfirm").toUpperCase(),
                        onClick: completeTest,
                    },
                ]}
            />

        </HeaderFooterLayout>
    )
}

export default Tests
